import React from 'react';
import { FieldRenderProps } from '@progress/kendo-react-form';
import { FormFieldWrapper } from '../FormFieldWrapper';
import {
  ListItemProps,
  MultiSelect,
  MultiSelectChangeEvent,
  MultiSelectFilterChangeEvent,
  TagData
} from '@progress/kendo-react-dropdowns';
import { useGlobalizationContext, useTranslation } from '@cs-cms/state-management';

const SELECT_ALL_CHOICE = '\u0000select-all';

interface FormMultiSelectProps extends FieldRenderProps {
  isLoading?: boolean;
  onFilterChange?: (value: string) => void;
  onSelectionChange?: (value: any) => void;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readSearchableText(value: unknown): string | undefined {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return undefined;
}

function getChoiceSearchText(choice: unknown): string {
  const primitiveText = readSearchableText(choice);
  if (primitiveText !== undefined) {
    return primitiveText;
  }

  if (!isRecord(choice)) {
    return '';
  }

  const candidates = [choice.label, choice.text, choice.name, choice.value];
  for (const candidate of candidates) {
    const text = readSearchableText(candidate);
    if (text !== undefined) {
      return text;
    }
  }

  return '';
}

function filterLocalChoices(choices: unknown, filterText: string): unknown[] {
  if (!Array.isArray(choices)) {
    return [];
  }

  const normalizedFilter = filterText.trim().toLowerCase();
  if (!normalizedFilter) {
    return choices;
  }

  return choices.filter(choice => getChoiceSearchText(choice).toLowerCase().includes(normalizedFilter));
}

function readFilterText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function isSelectAllChoice(choice: unknown): boolean {
  return choice === SELECT_ALL_CHOICE;
}

function toChoiceList(choices: unknown): unknown[] {
  return Array.isArray(choices) ? choices : [];
}

function areAllChoicesSelected(choices: readonly unknown[], selected: readonly unknown[]): boolean {
  if (choices.length === 0) {
    return false;
  }

  return choices.every(choice => selected.includes(choice));
}

function withoutSelectAll(values: readonly unknown[]): unknown[] {
  return values.filter(value => !isSelectAllChoice(value));
}

export const FormMultiSelect: React.FC<FormMultiSelectProps> = fieldRenderProps => {
  const { defaultValue, value, choices, name, disabled, isLoading, onFilterChange, onSelectionChange } =
    fieldRenderProps;
  const [filterText, setFilterText] = React.useState('');
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const selectAllLabel = translate('custom.common.selectAll');

  if (!name) {
    return <></>;
  }

  const usesExternalFilter = Boolean(onFilterChange);
  const allChoices = toChoiceList(choices);
  const filteredChoices = usesExternalFilter ? allChoices : filterLocalChoices(allChoices, filterText);
  const visibleChoices = allChoices.length > 0 ? [SELECT_ALL_CHOICE, ...filteredChoices] : filteredChoices;

  const deserializeValue = (raw: any): any[] => {
    if (Array.isArray(raw)) {
      return raw;
    }
    if (!raw) {
      return [];
    }
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed;
        }
        if (parsed) {
          return [parsed];
        }
      } catch {
        return [raw];
      }
    }
    return [];
  };

  const normalizedValue = deserializeValue(value);
  const normalizedDefaultValue = deserializeValue(defaultValue);
  const shouldUseDefaultValue = value === undefined || value === null || value === '';
  const selectedChoices = withoutSelectAll(shouldUseDefaultValue ? normalizedDefaultValue : normalizedValue);
  const allSelected = areAllChoicesSelected(allChoices, selectedChoices);
  const displayValue = allSelected ? [SELECT_ALL_CHOICE, ...selectedChoices] : selectedChoices;

  const renderItem = (element: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps): React.ReactNode => {
    if (!isSelectAllChoice(itemProps.dataItem)) {
      return element;
    }

    const className = [element.props.className, 'cpsMultiSelectSelectAll'].filter(Boolean).join(' ');

    return React.cloneElement(
      element,
      { className },
      <span className="k-list-item-text">{selectAllLabel}</span>
    );
  };

  const renderTag = (tagData: TagData, tag: React.ReactElement): React.ReactElement => {
    if (!tagData.data.some(item => isSelectAllChoice(item))) {
      return tag;
    }

    return <span key={tag.key} hidden className="cpsMultiSelectSelectAllTag" />;
  };

  const publishSelection = (nextValue: unknown[]): void => {
    if (onSelectionChange) {
      onSelectionChange(nextValue);
    }
  };

  const handleFilterChange = (event: MultiSelectFilterChangeEvent): void => {
    const nextFilter = readFilterText(event.filter.value);
    if (onFilterChange) {
      onFilterChange(nextFilter);
      return;
    }

    setFilterText(nextFilter);
  };

  const handleChange = (event: MultiSelectChangeEvent): void => {
    const nextValue = toChoiceList(event.target.value);
    const nextIncludesSelectAll = nextValue.some(item => isSelectAllChoice(item));
    const nextRealValues = withoutSelectAll(nextValue);

    if (nextIncludesSelectAll && !allSelected) {
      publishSelection([...allChoices]);
      return;
    }

    if (!nextIncludesSelectAll && allSelected && areAllChoicesSelected(allChoices, nextRealValues)) {
      publishSelection([]);
      return;
    }

    publishSelection(nextRealValues);
  };

  return (
    <FormFieldWrapper
      validationMessage={fieldRenderProps.validationMessage}
      value={fieldRenderProps.value}
      touched={fieldRenderProps.touched}
      id={fieldRenderProps.id}
      valid={fieldRenderProps.valid}
      disabled={fieldRenderProps.disabled}
      hint={fieldRenderProps.hint}
      label={fieldRenderProps.label}
      wrapperStyle={fieldRenderProps.wrapperStyle}
      optional={fieldRenderProps.optional}
      modified={fieldRenderProps.modified}
      visited={fieldRenderProps.visited}
      onChange={fieldRenderProps.onChange}
      onFocus={fieldRenderProps.onFocus}
      onBlur={fieldRenderProps.onBlur}
      name={fieldRenderProps.name}
      isLoading={isLoading}
    >
      <MultiSelect
        data={visibleChoices}
        value={displayValue}
        onChange={handleChange}
        onFilterChange={handleFilterChange}
        filterable={true}
        itemRender={renderItem}
        tagRender={renderTag}
        id={fieldRenderProps.name.toString()}
        validationMessage={fieldRenderProps.validationMessage || undefined}
        disabled={disabled}
      />
    </FormFieldWrapper>
  );
};
